import { QueryClient } from "react-query";
import { createStreamingStore, StreamingState } from "./StreamingStore";
import { createStore, StoreApi } from "zustand";
import { ChatConfiguration } from "../types";

export interface StreamManagerState {
  /** Lifecycle registry: dialogueId → live store, written by owner hooks. */
  byDialogue: Map<number, StoreApi<StreamingState>>;
  queryClient?: QueryClient;

  /**
   * Last-writer-wins lifecycle cache, not an identity: it lets a remounting
   * owner reattach to a live SSE store (useOwnChatStream resolves it) and
   * lets non-tree readers (useLiveWebExternals) find a dialogue's store.
   * Identity is the store object handed down via ChatStreamProvider. Call it
   * from an effect or event handler, never during render: every manager
   * subscriber is notified. A displaced store is not closed; a still-connected
   * one dev-warns, since it keeps its SSE until it ends on its own.
   *
   * Owners release their entry when they leave the store (unmount, dialogue
   * switch, newChat): at once when no turn is in flight, closing any idle
   * session, else when the turn ends. The registry never pins a transcript
   * nothing is showing, so there is no sweep.
   */
  register: (dialogueId: number, store: StoreApi<StreamingState>) => void;
  /** Read-only lookup; never creates. */
  resolve: (dialogueId: number) => StoreApi<StreamingState> | null;
  /**
   * Drop every entry that still points at `store`. Entries a later register()
   * already replaced are left alone, so a displaced store's release cannot
   * evict its successor.
   */
  unregister: (store: StoreApi<StreamingState>) => void;
  /**
   * Create an unregistered store wired to the query client. Safe to call
   * during render: nothing on the manager changes, so no subscriber is
   * notified mid-render. Owners register it from an effect.
   */
  create: (config?: ChatConfiguration) => StoreApi<StreamingState>;
  setQueryClient: (queryClient: QueryClient) => void;
}

/**
 * A turn is streaming or parked on a tool call: the send POST is in flight,
 * tokens are arriving, or the backend holds the turn for a card answer. Such
 * a store must stay resolvable so a returning owner reattaches to the
 * backgrounded stream instead of starting a second one. An open session with
 * no turn in flight is not live; its owner closes it on the way out.
 */
export const hasTurnInFlight = (store: StoreApi<StreamingState>) => {
  const state = store.getState();
  return state.isLoading || state.isTurnActive;
};

export const createStreamManagerStore = createStore<StreamManagerState>(
  (set, get) => ({
    byDialogue: new Map(),
    queryClient: undefined,

    register: (dialogueId: number, store: StoreApi<StreamingState>) => {
      const { byDialogue } = get();
      const displaced = byDialogue.get(dialogueId);
      if (displaced === store) return;
      if (displaced?.getState().isConnected && import.meta.env.DEV) {
        console.warn(
          `[StreamManager] register displaced a connected store for dialogue ` +
            `${dialogueId}; its SSE stays open until it ends`,
        );
      }
      const next = new Map(byDialogue);
      next.set(dialogueId, store);
      set({ byDialogue: next });
    },

    resolve: (dialogueId: number) => {
      return get().byDialogue.get(dialogueId) ?? null;
    },

    unregister: (store: StoreApi<StreamingState>) => {
      const { byDialogue } = get();
      let changed = false;
      const next = new Map(byDialogue);
      for (const [id, registered] of byDialogue) {
        if (registered === store) {
          next.delete(id);
          changed = true;
        }
      }
      if (changed) set({ byDialogue: next });
    },

    create: (config?: ChatConfiguration) => {
      const store = createStreamingStore(config || {});
      const { queryClient } = get();
      if (queryClient) store.getState().setQueryClient(queryClient);
      return store;
    },

    setQueryClient: (queryClient: QueryClient) => {
      set({ queryClient });
      for (const store of get().byDialogue.values()) {
        store.getState().setQueryClient(queryClient);
      }
    },
  }),
);
